import React from "react";
import { useTranslation } from "react-i18next";
import { type Locale } from "@/i18n";
import { constants } from "../../constants";
import { authApiClient } from "../../api/authApiClient";
import { CURRENT_USER } from "../../api/queries/currentUser";
import { useQuery } from "@apollo/client";
import { useOrg } from "@/hooks/useOrg";
import { Status } from "@/reactiveVars/chat";
import { Provider } from "@zeemee/react-web-chat";
import { useDebug } from "@/hooks/useDebug";

const {
  CHAT_API_URL,
  CHAT_WS_URL,
  GRAPHQL_API_URL,
  CHAT_MEDIA_S3_REGION,
  CHAT_MEDIA_S3_BUCKET,
} = constants;

type UseProviderConfigResult =
  | {
      status: Status.BOOTSTRAP_ERROR | Status.BOOTSTRAP_IN_PROGRESS;
      config?: undefined;
    }
  | {
      status: Status.BOOTSTRAP_SUCCESS;
      config: Omit<
        React.ComponentProps<typeof Provider>,
        "children" | "userDisplayWrapper"
      >;
    };

export const useProviderConfig = (): UseProviderConfigResult => {
  const { i18n } = useTranslation();

  const { error } = useDebug();

  const {
    data: currentUserData,
    loading: userLoading,
    error: currentUserError,
  } = useQuery(CURRENT_USER);
  const { org, error: currentOrgError, loading: orgLoading } = useOrg();

  const { uuid: userUuid } = currentUserData?.me || {};
  const { chatApiToken, chatWsToken, zeemeeApiToken } = authApiClient;
  const orgUuid = org?.uuid;

  if (userLoading || orgLoading) {
    return { status: Status.BOOTSTRAP_IN_PROGRESS };
  }

  if (
    !CHAT_API_URL ||
    !CHAT_WS_URL ||
    !GRAPHQL_API_URL ||
    !CHAT_MEDIA_S3_REGION ||
    !CHAT_MEDIA_S3_BUCKET
  ) {
    error(`ReactWebChatProvider: Missing API constants`, {
      CHAT_API_URL,
      CHAT_WS_URL,
      GRAPHQL_API_URL,
      CHAT_MEDIA_S3_REGION,
      CHAT_MEDIA_S3_BUCKET,
    });
    return { status: Status.BOOTSTRAP_ERROR };
  }

  if (currentUserError || currentOrgError) {
    error("ReactWebChatProvider: Error(s) retrieving user info", {
      currentUserError,
      currentOrgError,
    });
    return { status: Status.BOOTSTRAP_ERROR };
  }

  if (!chatApiToken || !chatWsToken || !zeemeeApiToken) {
    error("ReactWebChatProvider: Could not retrieve API tokens.", {
      chatApiToken,
      chatWsToken,
      zeemeeApiToken,
    });
    return { status: Status.BOOTSTRAP_ERROR };
  }

  if (!userUuid || !orgUuid) {
    error("ReactWebChatProvider: Could not retrieve userUuid or orgUuid", {
      userUuid,
      orgUuid,
    });
    return { status: Status.BOOTSTRAP_ERROR };
  }

  if (userLoading || orgLoading) {
    return { status: Status.BOOTSTRAP_IN_PROGRESS };
  }

  return {
    status: Status.BOOTSTRAP_SUCCESS,
    config: {
      locale: i18n.language as Locale,
      chatRestApiBaseUrl: CHAT_API_URL,
      chatRestApiJwt: chatApiToken,
      chatWsApiUrl: CHAT_WS_URL,
      chatWsApiJwt: chatWsToken,
      graphqlApiUrl: GRAPHQL_API_URL,
      graphqlApiJwt: zeemeeApiToken,
      userUuid: userUuid,
      orgUuid: orgUuid,
      mediaS3Region: CHAT_MEDIA_S3_REGION,
      mediaS3Bucket: CHAT_MEDIA_S3_BUCKET,
    },
  };
};
